import React, {
  useState,
  useEffect,
  useContext,
  createContext,
  useCallback,
  useRef,
} from 'react';
import api from 'services';
import { useAuth } from 'hooks/auth';
import { useNewToast } from 'hooks/newToast';
import {
  toDate,
  toPriceInt,
  moneyInputToReaisNonNegative,
} from 'utils/converters';
import {
  downloadColaboradoresCsvArquivo,
  montarParametrosExportColaboradores,
  normalizarLinhasExportColaboradores,
} from 'pages/Cadastros/Colaboradores/utils/exportColaboradoresCsv';
import moment from 'moment';
import { appWarnings } from 'utils/appWarnings';
import { buildEpiItensPayload } from 'utils/epiEntregaPayload';

const success = {
  type: 'success',
  message: '',
};

const colaboradoresDefault = {
  content: [],
  pagination: {},
};

const COLABORADORES = {
  content: [],
  total: 0,
};

const ColaboradoresContext = createContext({});

export function ColaboradoresProvider({ children }) {
  const { isAuth } = useAuth();

  const { showToast } = useNewToast();

  const [colaboradoresReduced, setColaboradoresReduced] = useState([]);
  const [colaboradores, setColaboradores] = useState(colaboradoresDefault);

  const [colaboradoresLoading, setColaboradoresLoading] = useState(false);

  const [colaborador, setColaborador] = useState({});
  const [colaboradorLoading, setColaboradorLoading] = useState(false);

  const [addColaboradorLoading, setAddColaboradorLoading] = useState(false);
  const [deleteColaboradorLoading, setDeleteColaboradorLoading] =
    useState(false);
  const [editColaboradorLoading, setEditColaboradorLoading] = useState(false);
  const [historicoColaboradorLoading, setHistoricoColaboradorLoading] =
    useState(false);
  const [historicoColaborador, setHistoricoColaborador] = useState([]);
  const [deleteAdiantamentoLoading, setDeleteAdiantamentoLoading] =
    useState(false);
  const [editAdiantamentoLoading, setEditAdiantamentoLoading] = useState(false);

  const [adiantamentos, setAdiantamentos] = useState([]);
  const [adiantamentosPagination, setAdiantamentosPagination] = useState(null);
  const [adiantamentosLoading, setAdiantamentosLoading] = useState(false);

  const [saldoMeeiro, setSaldoMeeiro] = useState({});
  const [saldoMeeiroLoading, setSaldoMeeiroLoading] = useState(false);

  const [meeiroHistoricoSaidas, setMeeiroHistoricoSaidas] = useState([]);
  const [meeiroHistoricoSaidasLoading, setMeeiroHistoricoSaidasLoading] =
    useState(false);

  const [meeiroHistoricoEntradas, setMeeiroHistoricoEntradas] = useState([]);
  const [meeiroHistoricoEntradasLoading, setMeeiroHistoricoEntradasLoading] =
    useState(false);

  const [talhoesByMeeiro, setTalhoesByMeeiro] = useState([]);
  const [talhoesByMeeiroLoading, setTalhoesByMeeiroLoading] = useState(false);
  const [addAdiantamentoLoading, setAddAdiantamentoLoading] = useState(false);
  const [addAdiantamentosLoteLoading, setAddAdiantamentosLoteLoading] =
    useState(false);

  const [addDescontosAdiantamentoLoading, setAddDescontosAdiantamentoLoading] =
    useState(false);

  const [colaboradoresReducedLoading, setColaboradoresReducedLoading] =
    useState(false);
  const [colaboradoresFolhaPagamento, setColaboradoresFolhaPagamento] =
    useState(COLABORADORES);
  const [
    colaboradoresFolhaPagamentoLoading,
    setColaboradoresFolhaPagamentoLoading,
  ] = useState(false);

  const [statusLoading, setStatusLoading] = useState(false);

  const [
    inativarColaboradoresEmMassaLoading,
    setInativarColaboradoresEmMassaLoading,
  ] = useState(false);

  const [
    atualizarSalarioBaseColaboradoresEmMassaLoading,
    setAtualizarSalarioBaseColaboradoresEmMassaLoading,
  ] = useState(false);

  const [exportColaboradoresCsvLoading, setExportColaboradoresCsvLoading] =
    useState(false);

  const lastColaboradoresListFilterRef = useRef(null);

  const [updateFileLoading, setUpdateFileLoading] = useState(false);

  const [attachments, setAttachments] = useState({
    content: [],
    pagination: undefined,
  });

  const [attachmentsLoading, setAttachmentsLoading] = useState(false);

  const [removeAttachmentLoading, setRemoveAttachmentLoading] = useState(false);

  const [historyCalendar, setHistoryCalendar] = useState({
    content: [],
    pagination: undefined,
    total: undefined,
  });
  const [historyCalendarLoading, setHistoryCalendarLoading] = useState(false);

  const [epiEntregas, setEpiEntregas] = useState([]);
  const [epiEntregasPagination, setEpiEntregasPagination] = useState({
    page: 1,
    pageSize: 30,
    total: 0,
  });
  const [epiEntregasLoading, setEpiEntregasLoading] = useState(false);
  const [printEpiRelatorioLoading, setPrintEpiRelatorioLoading] =
    useState(false);
  const [addEpiEntregaLoading, setAddEpiEntregaLoading] = useState(false);
  const [editEpiEntregaItemLoading, setEditEpiEntregaItemLoading] =
    useState(false);
  const [deleteEpiEntregaItemLoading, setDeleteEpiEntregaItemLoading] =
    useState(false);

  const down = useCallback(() => {
    setColaboradoresReduced([]);
    setColaboradores(colaboradoresDefault);
    setColaborador({});
    lastColaboradoresListFilterRef.current = null;
  }, []);

  const getColaboradores = useCallback(async (filter) => {
    try {
      if (filter != null && (filter?.reduced ?? 0) < 2) {
        lastColaboradoresListFilterRef.current = filter;
      }
      setColaboradoresLoading(true);
      const response = await api.colaborador().getColaboradores(filter);

      if (filter?.reduced >= 2) {
        setColaboradoresReduced(response);
      } else {
        setColaboradores(response);
      }
    } catch (errors) {
    } finally {
      setColaboradoresLoading(false);
    }
  }, []);

  const listColaboradoresFolhaPagamento = useCallback(async (filter) => {
    try {
      setColaboradoresFolhaPagamentoLoading(true);

      if (filter.tipoBusca === 1) {
        const response = await api
          .colaborador()
          .listColaboradoresFolhaPagamento(filter);
        setColaboradoresFolhaPagamento(response);
      } else {
        const content = await api
          .colaborador()
          .getColaboradores({ situacao: 'ATIVO', reduced: 1 });
        setColaboradoresFolhaPagamento({ content, total: content.length });
      }
    } catch {
    } finally {
      setColaboradoresFolhaPagamentoLoading(false);
    }
  }, []);

  const getColaborador = useCallback(async (id) => {
    try {
      setColaboradorLoading(true);
      const response = await api.colaboradores().idColaboradores(id);
      setColaborador(response);
    } catch (errors) {
    } finally {
      setColaboradorLoading(false);
    }
  }, []);

  const addColaborador = useCallback(
    async (data) => {
      try {
        setAddColaboradorLoading(true);

        // Função auxiliar para validar e converter data
        const validateAndConvertDate = (dateValue) => {
          if (
            !dateValue ||
            dateValue === '' ||
            dateValue === null ||
            dateValue === undefined
          ) {
            return null;
          }
          if (
            typeof dateValue === 'string' &&
            (dateValue === 'Data inválida' || dateValue === 'Invalid date')
          ) {
            return null;
          }
          const momentDate = moment(dateValue);
          if (!momentDate.isValid()) {
            return null;
          }
          return toDate(dateValue);
        };

        const newData = {
          nome: data?.nome,
          apelido: data?.apelido,
          codigo: data?.codigo,
          tipo: data?.tipo,
          funcao: data?.funcao?.id,
          cpf: data?.cpf,
          email: data?.email,
          nascimento: validateAndConvertDate(data?.nascimento),
          turma: data?.turma?.id,
          carteiraTrabalho: data?.carteiraTrabalho,
          celular: data?.celular,
          dataAdmissao: validateAndConvertDate(data?.dataAdmissao),
          dataDemissao: validateAndConvertDate(data?.dataDemissao),
          dependentes: data?.dependentes,
          filhos: data?.filhos,
          meeiroPorcentagem: Number(data?.meeiroPorcentagem) / 100,
          meeiroProporcao: Number(data?.meeiroProporcao || 0),
          meeiroSaldo: data?.meeiroSaldo?.id,
          meeiroTratos: data?.meeiroTratos?.id,
          obs: data?.obs,
          cargaHorariaMensal: data?.cargaHorariaMensal,
          pisPasep: data?.pisPasep,
          salarioBase: toPriceInt(data?.salarioBase),
          situacao: data?.situacao?.id,
          tags: data?.tags,
          telefone: data?.telefone,
          valorDia: toPriceInt(data?.valorDia),
          valorHora: toPriceInt(data?.valorHora),
          senha: data?.senha || null,
          fazendaId: data?.fazenda?.id || null,
          rg: data?.rg?.trim?.() ? data.rg.trim() : data?.rg || null,
          rgUf: data?.rgUf?.uf ?? null,
          rgEmissao: validateAndConvertDate(data?.rgEmissao),
          rgEmissor: data?.rgEmissor?.trim?.()
            ? data.rgEmissor.trim()
            : data?.rgEmissor || null,
          disponivelColetor: data?.disponivelColetor === true ? 1 : 0,
        };
        const response = await api.colaborador().addColaborador(newData);
        showToast({
          ...success,
          message: 'Colaborador adicionado com sucesso',
        });
        return response;
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setAddColaboradorLoading(false);
      }
    },
    [showToast],
  );

  const editColaborador = useCallback(
    async (data, id) => {
      try {
        setEditColaboradorLoading(true);

        const validateAndConvertDate = (dateValue) => {
          if (
            !dateValue ||
            dateValue === '' ||
            dateValue === null ||
            dateValue === undefined
          ) {
            return null;
          }
          if (
            typeof dateValue === 'string' &&
            (dateValue === 'Data inválida' || dateValue === 'Invalid date')
          ) {
            return null;
          }
          const momentDate = moment(dateValue);
          if (!momentDate.isValid()) {
            return null;
          }
          return toDate(dateValue);
        };

        const newData = {
          nome: data?.nome,
          apelido: data?.apelido,
          codigo: data?.codigo,
          tipo: data?.tipo,
          funcao: data?.funcao?.id,
          cpf: data?.cpf,
          email: data?.email,
          nascimento: validateAndConvertDate(data?.nascimento),
          turma: data?.turma?.id,
          carteiraTrabalho: data?.carteiraTrabalho,
          celular: data?.celular,
          dataAdmissao: validateAndConvertDate(data?.dataAdmissao),
          dataDemissao: validateAndConvertDate(data?.dataDemissao),
          dependentes: data?.dependentes,
          filhos: data?.filhos,
          meeiroPorcentagem: Number(data?.meeiroPorcentagem) / 100,
          meeiroProporcao: Number(data?.meeiroProporcao || 0),
          meeiroSaldo: data?.meeiroSaldo?.id,
          meeiroTratos: data?.meeiroTratos?.id,
          obs: data?.obs,
          cargaHorariaMensal: data?.cargaHorariaMensal,
          pisPasep: data?.pisPasep,
          salarioBase: toPriceInt(data?.salarioBase),
          situacao: data?.situacao?.id,
          tags: data?.tags,
          telefone: data?.telefone,
          valorDia: toPriceInt(data?.valorDia),
          valorHora: toPriceInt(data?.valorHora),
          senha: data?.senha || null,
          fazendaId: data?.fazenda?.id || null,
          rg: data?.rg?.trim?.() ? data.rg.trim() : data?.rg || null,
          rgUf: data?.rgUf?.uf ?? null,
          rgEmissao: validateAndConvertDate(data?.rgEmissao),
          rgEmissor: data?.rgEmissor?.trim?.()
            ? data.rgEmissor.trim()
            : data?.rgEmissor || null,
          disponivelColetor: data?.disponivelColetor === true ? 1 : 0,
        };
        const response = await api.colaborador().editColaborador(newData, id);
        showToast({ ...success, message: 'Colaborador editado com sucesso' });
        return response;
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setEditColaboradorLoading(false);
      }
    },
    [showToast],
  );

  const deleteColaborador = useCallback(
    async (id) => {
      try {
        setDeleteColaboradorLoading(true);
        await api.colaborador().deleteColaborador(id);
        showToast({ ...success, message: 'Colaborador removido com sucesso!' });
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setDeleteColaboradorLoading(false);
      }
    },
    [showToast],
  );

  const getHistoricoColaborador = useCallback(async (id, filter) => {
    try {
      const format_filter = {
        ...filter,
        atividade: filter?.atividade?.id,
      };

      setHistoricoColaboradorLoading(true);
      const response = await api
        .colaborador()
        .getHistoricoColaborador(id, format_filter);
      setHistoricoColaborador(response);
    } catch {
    } finally {
      setHistoricoColaboradorLoading(false);
    }
  }, []);

  const getAdiantamentos = useCallback(async (filter = {}) => {
    try {
      setAdiantamentosLoading(true);
      const { categoria, ...query } = filter;
      delete query.colaborador;
      delete query.dateStart;
      delete query.dateEnd;
      const response = await api.colaborador().getAdiantamentos({
        ...query,
        categoria: categoria?.id ?? categoria,
      });

      if (Array.isArray(response)) {
        setAdiantamentos(response);
        setAdiantamentosPagination(null);
      } else {
        setAdiantamentos(response?.content || []);
        setAdiantamentosPagination({
          ...(response?.pagination || {}),
          sumTotal: Number(response?.sumTotal || 0),
        });
      }
    } catch {
    } finally {
      setAdiantamentosLoading(false);
    }
  }, []);

  const addAdiantamento = useCallback(
    async (dados) => {
      try {
        setAddAdiantamentoLoading(true);
        const { data, valor, categoria, categoriaFinanceiro } = dados;
        const newData = {
          ...dados,
          data: toDate(data),
          valor: toPriceInt(valor),
          categoria: categoria?.id,
          categoriaFinanceiro: categoriaFinanceiro?.id,
          duplicationFrequency: dados?.duplicationFrequency?.id || '',
          duplicationPeriod: dados?.duplicationPeriod || 0,
        };
        const response = await api.colaborador().addAdiantamento(newData);
        showToast({
          ...success,
          message: 'Adiantamento adicionado com sucesso!',
        });
        return response;
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setAddAdiantamentoLoading(false);
      }
    },
    [showToast],
  );

  const addAdiantamentosLote = useCallback(
    async (dados) => {
      try {
        setAddAdiantamentosLoteLoading(true);
        const { data, categoria, categoriaFinanceiro, itens, gerarRecibo } =
          dados;
        const newData = {
          descricao: dados.descricao,
          data: toDate(data),
          categoria: categoria?.id,
          categoriaFinanceiro: categoriaFinanceiro?.id,
          gerarRecibo: Boolean(gerarRecibo),
          itens: (itens || []).map((item) => ({
            refId: item.refId,
            valor: toPriceInt(item.valor),
          })),
        };
        const response = await api.colaborador().addAdiantamentosLote(newData);
        showToast({
          ...success,
          message: `${response?.total || itens?.length || 0} adiantamento(s) adicionado(s) com sucesso!`,
        });
        return response;
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setAddAdiantamentosLoteLoading(false);
      }
    },
    [showToast],
  );

  const editAdiantamento = useCallback(
    async (dados, idAdiantamento) => {
      try {
        setEditAdiantamentoLoading(true);
        const { data, valor, categoria } = dados;
        const newData = {
          ...dados,
          data: toDate(data),
          valor: toPriceInt(valor),
          categoria: categoria?.id,
        };
        const response = await api
          .colaborador()
          .editAdiantamento(newData, idAdiantamento);
        showToast({ ...success, message: 'Adiantamento editado com sucesso!' });
        return response;
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setEditAdiantamentoLoading(false);
      }
    },
    [showToast],
  );

  const deleteAdiantamento = useCallback(
    async (id, filter) => {
      try {
        setDeleteAdiantamentoLoading(true);
        await api.colaborador().deleteAdiantamento(id, filter);
        showToast({
          ...success,
          message: 'Adiantamento removido com sucesso!',
        });
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setDeleteAdiantamentoLoading(false);
      }
    },
    [showToast],
  );

  const getColaboradoresReduced = useCallback(async (filter) => {
    try {
      setColaboradoresReducedLoading(true);
      const response = await api
        .colaborador()
        .getColaboradores({ ...filter, reduced: 1 });
      setColaboradoresReduced(response);
    } catch {
    } finally {
      setColaboradoresReducedLoading(false);
    }
  }, []);

  const changeStatusCollaborator = useCallback(
    async (id) => {
      try {
        setStatusLoading(true);
        await api.colaborador().changeStatusCollaborator(id);
        getColaborador(id);
      } catch {
      } finally {
        setStatusLoading(false);
      }
    },
    [getColaborador],
  );

  const inativarColaboradoresEmMassa = useCallback(
    async (ids) => {
      try {
        setInativarColaboradoresEmMassaLoading(true);
        const response = await api
          .colaborador()
          .inativarColaboradoresEmMassa({ ids });
        const count = response?.inativados ?? 0;
        showToast({
          ...success,
          message:
            count === 1
              ? '1 colaborador inativado com sucesso.'
              : `${count} colaboradores inativados com sucesso.`,
        });
        const last = lastColaboradoresListFilterRef.current;
        if (last != null) {
          await getColaboradores(last);
        }
        return response;
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw err;
      } finally {
        setInativarColaboradoresEmMassaLoading(false);
      }
    },
    [showToast, getColaboradores],
  );

  const atualizarSalarioBaseColaboradoresEmMassa = useCallback(
    async ({ ids, salarioBaseValor }) => {
      const salarioBase = moneyInputToReaisNonNegative(salarioBaseValor);
      try {
        setAtualizarSalarioBaseColaboradoresEmMassaLoading(true);
        const response = await api
          .colaborador()
          .atualizarSalarioBaseColaboradoresEmMassa({ ids, salarioBase });
        const count = response?.atualizados ?? 0;
        showToast({
          ...success,
          message:
            count === 1
              ? 'Salário base atualizado para 1 colaborador.'
              : `Salário base atualizado para ${count} colaboradores.`,
        });
        const last = lastColaboradoresListFilterRef.current;
        if (last != null) {
          await getColaboradores(last);
        }
        return response;
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw err;
      } finally {
        setAtualizarSalarioBaseColaboradoresEmMassaLoading(false);
      }
    },
    [showToast, getColaboradores],
  );

  const exportColaboradoresCsv = useCallback(
    async (filterOverride) => {
      try {
        setExportColaboradoresCsvLoading(true);
        const base = filterOverride ?? lastColaboradoresListFilterRef.current;
        const params = montarParametrosExportColaboradores(base);
        const response = await api.colaborador().exportColaboradores(params);
        const rows = normalizarLinhasExportColaboradores(response);
        if (rows.length === 0) {
          showToast({
            type: 'info',
            message: 'Nenhum colaborador encontrado para exportar.',
          });
          return;
        }
        downloadColaboradoresCsvArquivo(rows);
        showToast({
          ...success,
          message:
            rows.length === 1
              ? '1 colaborador exportado para CSV.'
              : `${rows.length} colaboradores exportados para CSV.`,
        });
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw err;
      } finally {
        setExportColaboradoresCsvLoading(false);
      }
    },
    [showToast],
  );

  useEffect(() => {
    if (!isAuth) {
      down();
    }
  }, [isAuth, down]);

  const addColaboradorReduzido = useCallback(
    async (data) => {
      try {
        setAddColaboradorLoading(true);
        const {
          nome,
          apelido,
          tipo,
          funcao,
          codigo,
          fazenda,
          senha,
          cpf,
          salarioBase,
          valorHora,
        } = data;
        const newData = {
          nome,
          apelido,
          tipo,
          funcao: funcao?.id,
          codigo,
          fazendaId: fazenda?.id || null,
          senha: senha || null,
          cpf: cpf || '',
          salarioBase: toPriceInt(salarioBase || ''),
          valorHora: toPriceInt(valorHora || ''),
          situacao: 'ATIVO',
        };
        const response = await api.colaborador().addColaborador(newData);
        showToast({
          ...success,
          message: 'Colaborador adicionado com sucesso',
        });

        await getColaboradores({ reduced: 2, situacao: 'ATIVO' });
        return response;
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setAddColaboradorLoading(false);
      }
    },
    [showToast, getColaboradores],
  );

  const editColaboradorReduzido = useCallback(
    async (data, id) => {
      try {
        setEditColaboradorLoading(true);
        const { nome, apelido, tipo, funcao, codigo } = data;
        const newData = {
          nome,
          apelido,
          tipo,
          funcao: funcao?.id,
          codigo,
        };
        const response = await api.colaborador().editColaborador(newData, id);
        showToast({ ...success, message: 'Colaborador editado com sucesso' });
        return response;
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setEditColaboradorLoading(false);
      }
    },
    [showToast],
  );

  const getSaldoMeeiro = useCallback(async (id, filter) => {
    try {
      setSaldoMeeiroLoading(true);
      const response = await api.colaborador().getSaldoMeeiro(id, filter);
      setSaldoMeeiro(response);
    } catch {
    } finally {
      setSaldoMeeiroLoading(false);
    }
  }, []);

  const getMeeiroHistoricoEntradas = useCallback(async (id, filter) => {
    try {
      setMeeiroHistoricoEntradasLoading(true);
      const response = await api
        .colaborador()
        .getMeeiroHistoricoEntradas(id, filter);
      setMeeiroHistoricoEntradas(response);
    } catch (errors) {
    } finally {
      setMeeiroHistoricoEntradasLoading(false);
    }
  }, []);

  const getMeeiroHistoricoSaidas = useCallback(async (id, filter) => {
    try {
      setMeeiroHistoricoSaidasLoading(true);
      const response = await api
        .colaborador()
        .getMeeiroHistoricoSaidas(id, filter);
      setMeeiroHistoricoSaidas(response);
    } catch (errors) {
    } finally {
      setMeeiroHistoricoSaidasLoading(false);
    }
  }, []);

  const geTalhoesByMeeiro = useCallback(async (id, filter) => {
    try {
      setTalhoesByMeeiroLoading(true);
      const response = await api.colaborador().geTalhoesByMeeiro(id, filter);
      setTalhoesByMeeiro(response);
    } catch (errors) {
    } finally {
      setTalhoesByMeeiroLoading(false);
    }
  }, []);

  const addDescontosAdiantamento = useCallback(async (data) => {
    try {
      setAddDescontosAdiantamentoLoading(true);
      await api.colaborador().addDescontosAdiantamento(data);
    } catch (errors) {
    } finally {
      setAddDescontosAdiantamentoLoading(false);
    }
  }, []);

  const updateFile = useCallback(async (collaboratorId, fileId) => {
    try {
      const data = {
        fileId,
      };

      setUpdateFileLoading(true);
      await api.colaborador().updateFile(collaboratorId, data);
    } catch (errors) {
    } finally {
      setUpdateFileLoading(false);
    }
  }, []);

  const listAttachments = useCallback(async (id, filters) => {
    try {
      setAttachmentsLoading(true);
      const response = await api.colaborador().listAttachments(id, filters);
      setAttachments(response);
    } catch (errors) {
    } finally {
      setAttachmentsLoading(false);
    }
  }, []);

  const removeAttachment = useCallback(async (colaboradorId, anexoId) => {
    try {
      setRemoveAttachmentLoading(true);
      await api.colaborador().removeAttachment(colaboradorId, anexoId);
    } catch (errors) {
    } finally {
      setRemoveAttachmentLoading(false);
    }
  }, []);

  const getHistoryCalendar = useCallback(async (id, filter) => {
    try {
      const format_filter = {
        ...filter,
        atividade: filter?.atividade?.id,
      };

      setHistoryCalendarLoading(true);
      const response = await api
        .colaborador()
        .getHistoricoColaborador(id, format_filter);
      setHistoryCalendar(response);
      return response;
    } catch {
      return undefined;
    } finally {
      setHistoryCalendarLoading(false);
    }
  }, []);

  const parseNullableDate = useCallback((value) => {
    if (!value) {
      return null;
    }

    const parsed = moment(value);
    if (!parsed.isValid()) {
      return null;
    }

    return toDate(value);
  }, []);

  const getEpiEntregas = useCallback(async (colaboradorId, filter) => {
    try {
      setEpiEntregasLoading(true);
      const response = await api
        .colaborador()
        .getColaboradorEpiEntregas(colaboradorId, filter);
      const normalizedResponse = Array.isArray(response)
        ? response
        : response?.content || [];
      setEpiEntregas(normalizedResponse);
      setEpiEntregasPagination(
        Array.isArray(response)
          ? {
              page: 1,
              pageSize: normalizedResponse.length || 30,
              total: normalizedResponse.length || 0,
            }
          : response?.pagination || { page: 1, pageSize: 30, total: 0 },
      );
    } catch {
      setEpiEntregas([]);
      setEpiEntregasPagination({ page: 1, pageSize: 30, total: 0 });
    } finally {
      setEpiEntregasLoading(false);
    }
  }, []);

  const getEpiRelatorio = useCallback(
    async (colaboradorId, filter) => {
      try {
        setPrintEpiRelatorioLoading(true);
        const reportFilter = {
          dateStart: filter?.dateStart || null,
          dateEnd: filter?.dateEnd || null,
        };
        const response = await api
          .colaborador()
          .getColaboradorEpiRelatorio(colaboradorId, reportFilter);
        return response;
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setPrintEpiRelatorioLoading(false);
      }
    },
    [showToast],
  );

  const getEpiRelatorioGlobal = useCallback(
    async (filter) => {
      try {
        setPrintEpiRelatorioLoading(true);
        const reportFilter = {};
        if (Number(filter?.colaboradorId) > 0) {
          reportFilter.colaboradorId = Number(filter.colaboradorId);
        }
        if (filter?.colaboradorNome) {
          reportFilter.colaboradorNome = filter.colaboradorNome;
        }
        if (filter?.gloInsumoId) {
          reportFilter.gloInsumoId = filter.gloInsumoId;
        }
        if (filter?.epi) {
          reportFilter.epi = filter.epi;
        }
        if (filter?.dateStart) {
          reportFilter.dateStart = filter.dateStart;
        }
        if (filter?.dateEnd) {
          reportFilter.dateEnd = filter.dateEnd;
        }
        const response = await api
          .colaborador()
          .getColaboradoresEpiRelatorioGlobal(reportFilter);
        return response;
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setPrintEpiRelatorioLoading(false);
      }
    },
    [showToast],
  );

  const addEpiEntrega = useCallback(
    async (colaboradorId, data) => {
      try {
        setAddEpiEntregaLoading(true);
        const rootDataEntrega = parseNullableDate(data?.dataEntrega);
        const payload = {
          itens: buildEpiItensPayload(
            data?.itens,
            rootDataEntrega,
            parseNullableDate,
          ),
        };

        const response = await api
          .colaborador()
          .addColaboradorEpiEntrega(colaboradorId, payload);
        showToast({
          ...success,
          message: 'Entrega de EPI adicionada com sucesso!',
        });
        return response;
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setAddEpiEntregaLoading(false);
      }
    },
    [parseNullableDate, showToast],
  );

  const editEpiEntregaItem = useCallback(
    async (colaboradorId, itemId, item) => {
      try {
        setEditEpiEntregaItemLoading(true);
        const payload = {
          gloAtividadeId: item?.atividade?.id || item?.gloAtividadeId || null,
          quantidade: Number(item?.quantidade || 0),
          dataEntrega: parseNullableDate(item?.dataEntrega),
          certificadoAprovacao: item?.certificadoAprovacao || null,
          dataValidadeEpi: parseNullableDate(item?.dataValidadeEpi),
          dataDevolucao: parseNullableDate(item?.dataDevolucao),
        };

        const gloInsumoId = item?.insumo?.id || item?.gloInsumoId || null;
        const itemDescricao =
          item?.itemEscrito || item?.itemDescricao || item?.itemInsumo || null;
        const useInsumo =
          typeof item?.usarInsumo === 'boolean'
            ? item.usarInsumo
            : !!gloInsumoId;

        payload.gloInsumoId = useInsumo ? gloInsumoId : null;
        payload.itemDescricao = useInsumo ? null : itemDescricao;

        const response = await api
          .colaborador()
          .editColaboradorEpiEntregaItem(colaboradorId, itemId, payload);
        showToast({ ...success, message: 'Item de EPI editado com sucesso!' });
        return response;
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setEditEpiEntregaItemLoading(false);
      }
    },
    [parseNullableDate, showToast],
  );

  const deleteEpiEntregaItem = useCallback(
    async (colaboradorId, itemId) => {
      try {
        setDeleteEpiEntregaItemLoading(true);
        await api
          .colaborador()
          .deleteColaboradorEpiEntregaItem(colaboradorId, itemId);
        showToast({ ...success, message: 'Item de EPI removido com sucesso!' });
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setDeleteEpiEntregaItemLoading(false);
      }
    },
    [showToast],
  );

  // Memoize the context value to avoid unnecessary re-renders
  const contextValue = React.useMemo(
    () => ({
      geTalhoesByMeeiro,
      talhoesByMeeiro,
      talhoesByMeeiroLoading,
      saldoMeeiro,
      saldoMeeiroLoading,
      getSaldoMeeiro,
      meeiroHistoricoSaidas,
      meeiroHistoricoSaidasLoading,
      getMeeiroHistoricoSaidas,
      meeiroHistoricoEntradas,
      meeiroHistoricoEntradasLoading,
      getMeeiroHistoricoEntradas,
      getColaboradores,
      addColaboradorReduzido,
      editColaboradorReduzido,
      getColaborador,
      colaboradorLoading,
      colaborador,
      setColaborador,
      colaboradores,
      colaboradoresLoading,
      colaboradoresReduced,
      getHistoricoColaborador,
      historicoColaboradorLoading,
      historicoColaborador,
      getAdiantamentos,
      adiantamentos,
      adiantamentosPagination,
      addColaborador,
      addColaboradorLoading,
      editColaboradorLoading,
      editColaborador,
      deleteColaboradorLoading,
      deleteColaborador,
      adiantamentosLoading,
      addAdiantamentoLoading,
      addAdiantamento,
      addAdiantamentosLoteLoading,
      addAdiantamentosLote,
      editAdiantamento,
      editAdiantamentoLoading,
      deleteAdiantamentoLoading,
      deleteAdiantamento,
      getColaboradoresReduced,
      addDescontosAdiantamento,
      addDescontosAdiantamentoLoading,
      colaboradoresReducedLoading,
      listColaboradoresFolhaPagamento,
      colaboradoresFolhaPagamento,
      colaboradoresFolhaPagamentoLoading,
      updateFile,
      updateFileLoading,
      changeStatusCollaborator,
      statusLoading,
      inativarColaboradoresEmMassa,
      inativarColaboradoresEmMassaLoading,
      atualizarSalarioBaseColaboradoresEmMassa,
      atualizarSalarioBaseColaboradoresEmMassaLoading,
      exportColaboradoresCsv,
      exportColaboradoresCsvLoading,
      listAttachments,
      attachments,
      attachmentsLoading,
      removeAttachment,
      removeAttachmentLoading,
      getHistoryCalendar,
      historyCalendar,
      historyCalendarLoading,
      epiEntregas,
      epiEntregasPagination,
      epiEntregasLoading,
      getEpiRelatorio,
      getEpiRelatorioGlobal,
      printEpiRelatorioLoading,
      getEpiEntregas,
      addEpiEntrega,
      addEpiEntregaLoading,
      editEpiEntregaItem,
      editEpiEntregaItemLoading,
      deleteEpiEntregaItem,
      deleteEpiEntregaItemLoading,
    }),
    [
      geTalhoesByMeeiro,
      talhoesByMeeiro,
      talhoesByMeeiroLoading,
      saldoMeeiro,
      saldoMeeiroLoading,
      getSaldoMeeiro,
      meeiroHistoricoSaidas,
      meeiroHistoricoSaidasLoading,
      getMeeiroHistoricoSaidas,
      meeiroHistoricoEntradas,
      meeiroHistoricoEntradasLoading,
      getMeeiroHistoricoEntradas,
      getColaboradores,
      addColaboradorReduzido,
      editColaboradorReduzido,
      getColaborador,
      colaboradorLoading,
      colaborador,
      setColaborador,
      colaboradores,
      colaboradoresLoading,
      colaboradoresReduced,
      getHistoricoColaborador,
      historicoColaboradorLoading,
      historicoColaborador,
      getAdiantamentos,
      adiantamentos,
      adiantamentosPagination,
      addColaborador,
      addColaboradorLoading,
      editColaboradorLoading,
      editColaborador,
      deleteColaboradorLoading,
      deleteColaborador,
      adiantamentosLoading,
      addAdiantamentoLoading,
      addAdiantamento,
      addAdiantamentosLoteLoading,
      addAdiantamentosLote,
      editAdiantamento,
      editAdiantamentoLoading,
      deleteAdiantamentoLoading,
      deleteAdiantamento,
      getColaboradoresReduced,
      addDescontosAdiantamento,
      addDescontosAdiantamentoLoading,
      colaboradoresReducedLoading,
      listColaboradoresFolhaPagamento,
      colaboradoresFolhaPagamento,
      colaboradoresFolhaPagamentoLoading,
      updateFile,
      updateFileLoading,
      changeStatusCollaborator,
      statusLoading,
      inativarColaboradoresEmMassa,
      inativarColaboradoresEmMassaLoading,
      atualizarSalarioBaseColaboradoresEmMassa,
      atualizarSalarioBaseColaboradoresEmMassaLoading,
      exportColaboradoresCsv,
      exportColaboradoresCsvLoading,
      listAttachments,
      attachments,
      attachmentsLoading,
      removeAttachment,
      removeAttachmentLoading,
      getHistoryCalendar,
      historyCalendar,
      historyCalendarLoading,
      epiEntregas,
      epiEntregasPagination,
      epiEntregasLoading,
      getEpiRelatorio,
      getEpiRelatorioGlobal,
      printEpiRelatorioLoading,
      getEpiEntregas,
      addEpiEntrega,
      addEpiEntregaLoading,
      editEpiEntregaItem,
      editEpiEntregaItemLoading,
      deleteEpiEntregaItem,
      deleteEpiEntregaItemLoading,
    ],
  );

  return (
    <ColaboradoresContext.Provider value={contextValue}>
      {children}
    </ColaboradoresContext.Provider>
  );
}

export function useColaborador() {
  const context = useContext(ColaboradoresContext);

  if (!context) throw new Error('useMenu must be used within an ToastProvider');

  return context;
}
